/*eslint max-lines-per-function: ["error", 219]*/

import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import type { MultiValue } from 'react-select';
import Select from 'react-select';

import FormRow from '../../../app/components/FormRow';
import { Option } from '../Selects/Selector';
import { CreateUpdateExcelConfigDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import TeamSelector from '../Selects/TeamSelector';
import { usePokCore } from '../../../common/hooks/usePokCore';
import ProfileSelector from '../Selects/ProfileSelector';
import { PermissionsEnum } from '../../../common/pokCore/authorization/permissions';
import { useAuth } from '../../../common/hooks/useAuth';
import ProfileCategorySelector from '../Selects/ProfileCategorySelector';
import {
  ExcelImportTypeEnum,
  ExcelImportTypeObj,
} from '../../../common/pokCore/validation/enums';
import TeamAllSelector from '../Selects/TeamAllSelector';
import QuillTextEditor from '../../../app/components/QuillTextEditor';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { TooltipKey } from '../../../common/pokCore/tooltip.enum';

interface ExcelConfigFormProps {
  id?: string;
  refresh?: boolean;
  config: CreateUpdateExcelConfigDto;
  propertyChange?: (obj: Partial<CreateUpdateExcelConfigDto>) => void;
  readOnly?: boolean;
}

export const ExcelConfigForm: React.FC<ExcelConfigFormProps> = props => {
  const pok = usePokCore();
  const auth = useAuth();
  const { t, tk } = useTranslation('excelImportLng');

  const getOptionValues = (
    options: Option | MultiValue<Option> | null | undefined,
  ) => (options ? (options as Option[]).map(({ value }) => value) : []);

  const getOptionValue = (
    options: Option | MultiValue<Option> | null | undefined,
  ) => (options ? (options as Option).value : undefined);

  const isAdmin = auth.check(PermissionsEnum.Administration);

  const emailsJoined = (props.config.emails ?? []).join(', ');
  const [emails, setEmails] = useState(emailsJoined);

  useEffect(() => {
    setEmails(emailsJoined);
  }, [emailsJoined]);

  const propertyChange = (obj: Partial<CreateUpdateExcelConfigDto>) => {
    if (
      obj.description &&
      ['<p><br></p>', '<h1><br></h1>', '<h2><br></h2>'].includes(
        obj.description,
      )
    ) {
      obj.description = '';
    }
    if (props.propertyChange) {
      props.propertyChange(obj);
    }
  };

  const importOptions = Object.entries(ExcelImportTypeEnum).map(
    ([key, value]) => ({
      value: key as ExcelImportTypeEnum,
      label: t(ExcelImportTypeObj[value]),
    }),
  );

  return (
    <>
      <Form className="d-grid gap-3">
        <FormRow controlId="name" label={t(tk.common.name)}>
          <Form.Control
            type="text"
            value={props.config?.name || ''}
            onChange={e => propertyChange({ name: e.target.value })}
            disabled={props.readOnly}
          />
        </FormRow>
        <FormRow controlId="type" label={t(tk.config.importConfigType)}>
          <Select
            value={
              props.config.type
                ? importOptions.find(({ value }) => props.config.type === value)
                : null
            }
            options={importOptions}
            onChange={option =>
              propertyChange({
                type: option?.value,
              })
            }
            isDisabled={props.readOnly}
            placeholder={t(tk.component.pointOut)}
          />
        </FormRow>
        <FormRow controlId="team" label={t(tk.common.team)}>
          {props.config.companyIndependent && isAdmin ? (
            <TeamAllSelector
              onChange={options =>
                propertyChange({
                  excelConfigTeamsIds: getOptionValues(options),
                })
              }
              value={props.config.excelConfigTeamsIds || []}
              readOnly={props.readOnly}
              includeAssignedInactiveTeams
            />
          ) : (
            <TeamSelector
              isMulti={true}
              value={props.config.excelConfigTeamsIds || []}
              companyId={pok.getCompanyId()}
              permission={PermissionsEnum.Projects}
              clearable={true}
              onChange={options => {
                propertyChange({
                  excelConfigTeamsIds: getOptionValues(options),
                });
              }}
              readOnly={props.readOnly}
              includeAssignedInactiveTeams
            />
          )}
        </FormRow>
        <FormRow controlId="profile" label={t(tk.config.profile)}>
          <ProfileSelector
            value={props.config.profileId || ''}
            companyIds={[pok.getCompanyId()]}
            onChange={options => {
              propertyChange({
                profileId: getOptionValue(options),
              });
            }}
            readOnly={
              props.readOnly || !(props.config.excelConfigTeamsIds?.length > 0)
            }
          />
        </FormRow>
        {props.config.profileId && (
          <FormRow controlId="profileCat" label={t(tk.config.profileCategory)}>
            <ProfileCategorySelector
              profileId={props.config.profileId}
              value={props.config.profileCategoryId || ''}
              onChange={option =>
                propertyChange({
                  profileCategoryId: option?.value,
                })
              }
              autoSelect={false}
              readOnly={props.readOnly}
            />
          </FormRow>
        )}
        <FormRow
          controlId="description"
          label={t(tk.config.importConfigDescription)}
        >
          <QuillTextEditor
            value={props.config?.description}
            readOnly={props.readOnly}
            onChange={val => propertyChange({ description: val })}
            uniqueKey="description"
          />
        </FormRow>
        <FormRow
          controlId="emails"
          label={t(tk.config.emails)}
          tooltipKey={TooltipKey.emailsTooltip}
        >
          <Form.Control
            type="text"
            value={emails}
            onChange={e => setEmails(e.target.value)}
            onBlur={() =>
              propertyChange({
                emails: emails
                  .split(',')
                  .map(s => s.trim())
                  .filter(Boolean),
              })
            }
            disabled={props.readOnly}
          />
        </FormRow>
        <FormRow
          controlId="companyIndependent"
          label={t(tk.config.companyIndependent)}
        >
          <Form.Check
            type="checkbox"
            className="pt-2"
            checked={props.config.companyId ? false : true}
            onChange={e =>
              propertyChange({ companyIndependent: e.target.checked })
            }
            disabled={props.readOnly || !isAdmin}
          />
        </FormRow>
        <FormRow controlId="updateExisting" label={t(tk.config.updateExisting)}>
          <Form.Check
            type="checkbox"
            className="pt-2"
            checked={props.config.updateExisting ?? true}
            onChange={e => propertyChange({ updateExisting: e.target.checked })}
            disabled={props.readOnly}
          />
        </FormRow>
        <FormRow
          controlId="setReadyToInvoice"
          label={t(tk.config.setReadyToInvoice)}
          tooltipKey={TooltipKey.setReadyToInvoiceTooltip}
        >
          <Form.Check
            type="checkbox"
            className="pt-2"
            checked={props.config.setReadyToInvoice ?? false}
            onChange={e =>
              propertyChange({ setReadyToInvoice: e.target.checked })
            }
            disabled={props.readOnly}
          />
        </FormRow>
        <FormRow controlId="active" label={t(tk.common.activeHe)}>
          <Form.Check
            type="checkbox"
            className="pt-2"
            checked={props.config.active ?? true}
            onChange={e => propertyChange({ active: e.target.checked })}
            disabled={props.readOnly}
          />
        </FormRow>
      </Form>
    </>
  );
};
